import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Droplet, 
  TestTube2,
  FlaskConical,
  ScanLine,
  Layers,
  Microscope,
  ChevronRight,
  Target,
  HelpCircle,
  BarChart3,
  ArrowRight,
  LinkIcon,
  Beaker,
  Eye,
  Activity,
  Crosshair
} from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import UnifiedSearch from '../components/UnifiedSearch';
import LanguageToggle from '../components/LanguageToggle';
import { testsData } from '../data/testsContent';
import { t } from '../utils/translations';

// Spanish translations for test titles and summaries
const testTranslations = {
  'psa-blood-test': {
    title: 'Prueba de PSA (Antígeno Prostático Específico)',
    summary: 'Análisis de sangre que mide el antígeno prostático específico, utilizado para detección y monitoreo del cáncer de próstata.'
  },
  'urinalysis': {
    title: 'Análisis de Orina',
    summary: 'Análisis completo de orina para detectar infecciones, sangre y otras anomalías.'
  },
  'urine-culture': {
    title: 'Cultivo de Orina',
    summary: 'Identifica bacterias específicas que causan infecciones urinarias para guiar el tratamiento con antibióticos.'
  },
  'prostate-mri': {
    title: 'Resonancia Magnética de Próstata',
    summary: 'Imágenes detalladas de la próstata usando tecnología de resonancia magnética para evaluar anomalías.'
  },
  'ct-kidney-stones': {
    title: 'TC para Cálculos Renales',
    summary: 'Tomografía computarizada especializada para detectar y evaluar cálculos renales.'
  },
  'semen-analysis': {
    title: 'Análisis de Semen',
    summary: 'Evalúa la salud y viabilidad del esperma para evaluación de fertilidad.'
  },
  'cystoscopy': {
    title: 'Cistoscopia',
    summary: 'Examen visual del interior de la vejiga usando una cámara pequeña.'
  },
  'urodynamic-studies': {
    title: 'Estudios Urodinámicos',
    summary: 'Pruebas completas que evalúan cómo funcionan la vejiga, esfínteres y uretra.'
  },
  'scrotal-ultrasound': {
    title: 'Ultrasonido Escrotal',
    summary: 'Imágenes por ultrasonido del escroto para evaluar masas, dolor o infertilidad.'
  },
  'testosterone-blood-test': {
    title: 'Prueba de Testosterona en Sangre',
    summary: 'Análisis de sangre que mide los niveles de testosterona para evaluar salud hormonal.'
  },
  'kidney-function-panel': {
    title: 'Panel de Función Renal (BMP/CMP)',
    summary: 'Análisis de sangre que miden qué tan bien los riñones filtran los desechos.'
  },
  'complete-blood-count': {
    title: 'Conteo Sanguíneo Completo (CSC)',
    summary: 'Análisis de sangre común que mide glóbulos rojos, blancos y plaquetas.'
  },
  '24-hour-urine': {
    title: 'Recolección de Orina de 24 Horas',
    summary: 'Mide minerales y sustancias que forman cálculos durante un período de 24 horas.'
  },
  'urine-cytology': {
    title: 'Citología de Orina',
    summary: 'Examen microscópico de células urinarias para detectar cáncer de vejiga.'
  },
  'urine-pcr': {
    title: 'PCR de Orina',
    summary: 'Prueba molecular que detecta material genético de patógenos en la orina.'
  },
  'urine-fish': {
    title: 'FISH de Orina',
    summary: 'Prueba de hibridación fluorescente para detectar anomalías cromosómicas en células urinarias.'
  },
  'renal-ultrasound': {
    title: 'Ultrasonido Renal',
    summary: 'Imágenes por ultrasonido de los riñones para detectar cálculos, quistes o bloqueos.'
  },
  'ct-urogram': {
    title: 'TC Urograma',
    summary: 'Imágenes detalladas de riñones, uréteres y vejiga usando tecnología de TC con contraste.'
  },
  'bone-scan': {
    title: 'Gammagrafía Ósea',
    summary: 'Examen de medicina nuclear para detectar propagación del cáncer a los huesos.'
  },
  'psma-pet-scan': {
    title: 'PET-PSMA',
    summary: 'Imágenes avanzadas para detectar cáncer de próstata recurrente o metastásico.'
  },
  'prostate-biopsy': {
    title: 'Biopsia de Próstata',
    summary: 'Muestra de tejido prostático para diagnosticar o descartar cáncer de próstata.'
  },
  'uroflow-pvr': {
    title: 'Uroflujometría y Residuo Postmiccional',
    summary: 'Mide la velocidad del flujo urinario y la orina residual después de orinar.'
  },
  'renal-mass-biopsy': {
    title: 'Biopsia de Masa Renal',
    summary: 'Muestra de tejido de una masa renal para determinar si es cancerosa.'
  },
  'post-vasectomy-semen-analysis': {
    title: 'Análisis de Semen Post-Vasectomía',
    summary: 'Confirma el éxito de la vasectomía verificando la ausencia de esperma.'
  },
  'sti-panel': {
    title: 'Panel de ITS',
    summary: 'Pruebas para detectar infecciones de transmisión sexual comunes.'
  }
};

// Category translations
const categoryTranslations = {
  'Blood Test': 'Análisis de Sangre',
  'Urine Test': 'Análisis de Orina',
  'Imaging': 'Imágenes',
  'Procedure': 'Procedimientos',
  'Fertility Test': 'Pruebas de Fertilidad'
};

// UI text translations for test pages
const uiText = {
  en: {
    testsTitle: 'Common Urologic Tests',
    testsDesc: 'Understand what tests measure, why they\'re ordered, and what results mean.',
    searchPlaceholder: 'Search tests, labs, imaging...',
    allTests: 'All Tests',
    whatMeasures: 'What This Test Measures',
    whyOrdered: 'Why Doctors Order This Test',
    understandResults: 'Understanding Your Results',
    whatHappensNext: 'What Happens Next',
    relatedConditions: 'Related Conditions',
    relatedConditionsIntro: 'Learn more about conditions related to this test:',
    learnMore: 'Learn more',
    notFound: 'Test Not Found',
    notFoundDesc: "The test page you're looking for doesn't exist.",
    backToTests: 'Back to Tests',
    disclaimer: 'This content is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. Test interpretation should always be done by a qualified healthcare provider who knows your complete medical history.',
    note: 'Note',
    noteText: 'Test preparation and procedures may vary by facility. Always follow the specific instructions provided by your healthcare provider.'
  },
  es: {
    testsTitle: 'Pruebas Urológicas Comunes',
    testsDesc: 'Entienda qué miden las pruebas, por qué se ordenan y qué significan los resultados.',
    searchPlaceholder: 'Buscar pruebas, laboratorios, imágenes...',
    allTests: 'Todas las Pruebas',
    whatMeasures: 'Qué Mide Esta Prueba',
    whyOrdered: 'Por Qué Los Médicos Ordenan Esta Prueba',
    understandResults: 'Entendiendo Sus Resultados',
    whatHappensNext: 'Qué Sucede Después',
    relatedConditions: 'Condiciones Relacionadas',
    relatedConditionsIntro: 'Aprenda más sobre las condiciones relacionadas con esta prueba:',
    learnMore: 'Más información',
    notFound: 'Prueba No Encontrada',
    notFoundDesc: 'La página de prueba que busca no existe.',
    backToTests: 'Volver a Pruebas',
    disclaimer: 'Este contenido es solo para fines educativos y no sustituye el consejo, diagnóstico o tratamiento médico profesional. La interpretación de pruebas siempre debe ser realizada por un proveedor de salud calificado que conozca su historial médico completo.',
    note: 'Nota',
    noteText: 'La preparación y los procedimientos de las pruebas pueden variar según el centro. Siempre siga las instrucciones específicas proporcionadas por su proveedor de atención médica.'
  }
};

// Icon mapping
const iconMap = {
  'droplet': Droplet,
  'test-tube': TestTube2,
  'flask': FlaskConical,
  'scan': ScanLine,
  'layers': Layers,
  'microscope': Microscope,
  'eye': Eye,
  'activity': Activity,
  'crosshair': Crosshair
};

// Tests Index Page
export const TestsIndexPage = () => {
  const [language, setLanguage] = useState('en');
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  const getText = (key) => uiText[language][key];

  // Group tests by category
  const categories = {};
  testsData.forEach(test => {
    if (!categories[test.category]) {
      categories[test.category] = [];
    }
    categories[test.category].push(test);
  });

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: language === 'en' ? 'Home' : 'Inicio', path: '/' },
        { label: language === 'en' ? 'Tests' : 'Pruebas', path: '/tests' }
      ]} />

      <div className="page-header-centered" data-testid="tests-index">
        <div className="page-header-top">
          <h1>{getText('testsTitle')}</h1>
          <LanguageToggle language={language} onToggle={toggleLanguage} />
        </div>
        <p>{getText('testsDesc')}</p>
      </div>

      {/* Search Bar */}
      <div className="hub-search-wrapper">
        <UnifiedSearch placeholder={getText('searchPlaceholder')} variant="hub" />
      </div>

      {Object.entries(categories).map(([category, tests]) => {
        const translatedCategory = language === 'es' && categoryTranslations[category] 
          ? categoryTranslations[category] 
          : category;
        return (
          <div key={category} className="tests-category-section">
            <h2 className="tests-category-title">{translatedCategory}</h2>
            <div className="tests-grid" data-testid={`tests-grid-${category.toLowerCase().replace(' ', '-')}`}>
              {tests.map(test => {
                const IconComponent = iconMap[test.icon] || Beaker;
                const translation = testTranslations[test.id];
                return (
                  <Link 
                    to={`/tests/${test.id}`}
                    key={test.id}
                    className="test-card"
                    data-testid={`test-card-${test.id}`}
                  >
                    <div className="test-card-icon">
                      <IconComponent size={26} />
                    </div>
                    <div className="test-card-content">
                      <h3>{language === 'es' && translation ? translation.title : test.title}</h3>
                      <p>{language === 'es' && translation ? translation.summary : test.summary}</p>
                    </div>
                    <ChevronRight size={20} className="test-card-arrow" />
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="tests-info-note">
        <HelpCircle size={20} />
        <p>
          <strong>{getText('note')}:</strong> {getText('noteText')}
        </p>
      </div>
    </PageWrapper>
  );
};

// Individual Test Page
const TestPage = () => {
  const { testId } = useParams();
  const [language, setLanguage] = useState('en');
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [testId]);

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  const getText = (key) => uiText[language][key];

  const test = testsData.find(t => t.id === testId);

  if (!test) {
    return (
      <PageWrapper>
        <div className="empty-state">
          <h2>{getText('notFound')}</h2>
          <p>{getText('notFoundDesc')}</p>
          <Link to="/tests" className="btn-primary">
            {getText('backToTests')}
          </Link>
        </div>
      </PageWrapper>
    );
  }

  const IconComponent = iconMap[test.icon] || Beaker;
  const { content } = test;
  const translation = testTranslations[testId];
  const translatedCategory = language === 'es' && categoryTranslations[test.category] 
    ? categoryTranslations[test.category] 
    : test.category;

  // Helper function to render markdown-like content
  const renderContent = (text) => {
    return text.split('\n\n').map((paragraph, idx) => {
      // Check if it's a table
      if (paragraph.includes('|') && paragraph.includes('---')) {
        const lines = paragraph.trim().split('\n');
        const headers = lines[0].split('|').filter(cell => cell.trim());
        const rows = lines.slice(2).map(line => 
          line.split('|').filter(cell => cell.trim())
        );
        
        return (
          <div key={idx} className="test-table-wrapper">
            <table className="test-table">
              <thead>
                <tr>
                  {headers.map((header, i) => (
                    <th key={i}>{header.trim()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>{cell.trim()}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      
      return (
        <p key={idx} dangerouslySetInnerHTML={{ 
          __html: paragraph
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            .replace(/\n- /g, '</li><li>')
            .replace(/^- /, '<ul><li>')
            .replace(/<\/li>$/, '</li></ul>')
            .replace(/\n/g, '<br />')
        }} />
      );
    });
  };

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: language === 'en' ? 'Home' : 'Inicio', path: '/' },
        { label: language === 'en' ? 'Tests' : 'Pruebas', path: '/tests' },
        { label: language === 'es' && translation ? translation.title : test.title, path: `/tests/${test.id}` }
      ]} />

      <div className="test-page" data-testid={`test-page-${test.id}`}>
        {/* Header */}
        <div className="test-header">
          <div className="test-header-nav">
            <Link to="/tests" className="back-link">
              <ArrowLeft size={20} />
              <span>{getText('allTests')}</span>
            </Link>
            <LanguageToggle language={language} onToggle={toggleLanguage} />
          </div>
          
          <div className="test-title-section">
            <div className="test-icon-large">
              <IconComponent size={32} />
            </div>
            <div>
              <span className="test-category-badge">{translatedCategory}</span>
              <h1>{language === 'es' && translation ? translation.title : test.title}</h1>
            </div>
          </div>
          <p className="test-summary">{language === 'es' && translation ? translation.summary : test.summary}</p>
        </div>

        {/* What It Measures */}
        <section className="test-section" data-testid="section-what-measures">
          <div className="section-header">
            <Target size={22} />
            <h2>{getText('whatMeasures')}</h2>
          </div>
          <div className="section-content markdown-content">
            {renderContent(content.whatItMeasures)}
          </div>
        </section>

        {/* Why Ordered */}
        <section className="test-section" data-testid="section-why-ordered">
          <div className="section-header">
            <HelpCircle size={22} />
            <h2>{getText('whyOrdered')}</h2>
          </div>
          <div className="section-content markdown-content">
            {renderContent(content.whyOrdered)}
          </div>
        </section>

        {/* Results Interpretation */}
        <section className="test-section results-section" data-testid="section-results">
          <div className="section-header">
            <BarChart3 size={22} />
            <h2>{getText('understandResults')}</h2>
          </div>
          <div className="section-content markdown-content">
            {renderContent(content.resultsInterpretation)}
          </div>
        </section>

        {/* Next Steps */}
        <section className="test-section" data-testid="section-next-steps">
          <div className="section-header">
            <ArrowRight size={22} />
            <h2>{getText('whatHappensNext')}</h2>
          </div>
          <div className="section-content markdown-content">
            {renderContent(content.nextSteps)}
          </div>
        </section>

        {/* Related Conditions */}
        <section className="test-section" data-testid="section-related-conditions">
          <div className="section-header">
            <LinkIcon size={22} />
            <h2>{getText('relatedConditions')}</h2>
          </div>
          <p className="section-intro">{getText('relatedConditionsIntro')}</p>
          <div className="related-conditions-list">
            {content.relatedConditions.map((condition, idx) => (
              <Link 
                key={idx}
                to={`/learn/${condition.articleId}`}
                className="related-condition-link"
                data-testid={`related-condition-${idx}`}
              >
                <span>{condition.title}</span>
                <ChevronRight size={18} />
              </Link>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <div className="test-disclaimer">
          <p>
            <strong>{language === 'en' ? 'Disclaimer:' : 'Aviso:'}</strong> {getText('disclaimer')}
          </p>
        </div>
      </div>
    </PageWrapper>
  );
};

export default TestPage;
